// Canonical inventory under each owner; received shares are references, never extra resources in the totals.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { api, HttpError, type AccessEvidence, type FleetOverview, type FleetResource, type Me } from "./api";

const stamp = (at: number) => new Date(at * 1000).toLocaleString();
const accountKey = (email: string) => `account:${email}`;
const resourceKey = (kind: string, id: string) => `${kind}:${id}`;
const sessionLabel = { open: "Session open", disconnected: "Disconnected · power unknown", connecting: "Connecting", closing: "Closing" };

function AccessTime({ value }: { value: AccessEvidence | null }) {
  return value ? <><time dateTime={new Date(value.at * 1000).toISOString()}>{stamp(value.at)}</time>
    <small>{value.source} · {value.actor}</small></> : <span className="muted">No recorded access</span>;
}

type Row = { key: string; parent?: string; level: number; title: string; kind: string; branch?: boolean;
  relation: ReactNode; state: ReactNode; last: AccessEvidence | null; jump?: string; jumpOwner?: string };

export function Overview({ me }: { me: Me }) {
  // Also guard direct component use. This is presentation only; the server independently authorizes every request.
  return me.superadmin === true ? <Inventory me={me} /> : null;
}

function Inventory({ me }: { me: Me }) {
  const [data, setData] = useState<FleetOverview | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focused, setFocused] = useState("");
  const [now, setNow] = useState(Date.now() / 1000);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const jumpPending = useRef("");

  useEffect(() => {
    let live = true, busy = false, forbidden = false;
    const load = async () => {
      if (busy || forbidden) return;
      busy = true;
      setLoading(true);
      try {
        const result = await api.overview();
        if (live) { setData(result); setError(""); setDenied(false); setNow(Date.now() / 1000); }
      } catch (e) {
        if (live) {
          forbidden = e instanceof HttpError && (e.status === 401 || e.status === 403);
          if (forbidden) { setData(null); setDenied(true); }
          setError(e instanceof Error ? e.message : "Could not load the overview");
        }
      } finally { busy = false; if (live) setLoading(false); }
    };
    void load();
    const poll = window.setInterval(() => { void load(); }, 15000);
    const clock = window.setInterval(() => setNow(Date.now() / 1000), 5000);
    return () => { live = false; clearInterval(poll); clearInterval(clock); };
  }, [refresh]);

  useEffect(() => {
    if (jumpPending.current) {
      rowRefs.current.get(jumpPending.current)?.focus();
      jumpPending.current = "";
    }
  });

  const stale = !!error || (data !== null && now - data.generated_at > 30);
  const q = query.trim().toLowerCase();
  const open = (key: string) => q !== "" || expanded.has(key);
  const toggle = (key: string) => setExpanded((old) => {
    const next = new Set(old); if (next.has(key)) next.delete(key); else next.add(key); return next;
  });
  const resources = data ? [
    ...data.computers.map((r) => ({ ...r, kind: "computer" as const })),
    ...data.kvms.map((r) => ({ ...r, kind: "kvm" as const })),
  ] : [];
  const computerNames = new Map(data?.computers.map((c) => [c.id, c.name]));
  const kvmNames = new Map(data?.kvms.map((k) => [k.id, k.name]));
  const paths = new Map(data?.paths.map((p) => [p.id, p]));
  const known = new Set(data?.accounts.map((a) => a.email));
  const missingOwners = [...new Set(resources.filter((r) => !known.has(r.owner)).map((r) => r.owner))].sort();
  const groups = [
    ...(data?.accounts.map((a) => ({ email: a.email, account: a })) ?? []),
    ...missingOwners.map((email) => ({ email, account: null })),
  ];
  const allBranches = [...groups.map((g) => accountKey(g.email)), ...resources.map((r) => resourceKey(r.kind, r.id))];
  const rows: Row[] = [];
  const resourceText = (r: typeof resources[number]) => [r.name, r.id, r.owner, r.kind,
    ...r.shares.map((s) => `${s.email} ${s.level}`), ...r.path_ids.map((id) => {
      const p = paths.get(id); return `${id} ${p?.kind} ${kvmNames.get(p?.kvm_id ?? "") ?? ""}`;
    })].join(" ").toLowerCase();
  const state = (r: FleetResource) => <>
    <span>{stale ? "Snapshot stale" : sessionLabel[r.session]}</span>
    {!stale && r.holds.filter((h) => h.expires_at > now).map((h) => <small key={h.target_id}>
      Held by {h.holder} · {h.via}<br />Since {stamp(h.since)}<br />Heartbeat {stamp(h.heartbeat)}
    </small>)}
  </>;
  const shares = (r: FleetResource) => r.shares.length > 0
    ? <small>Shared to {r.shares.map((s) => `${s.email} (${s.level})`).join(", ")}</small>
    : <small>Not explicitly shared</small>;

  // "View as": the whole app becomes that user's (their computers, settings, iPhone pairing) until Stop in the top bar.
  const viewAs = (email: string) => {
    api.viewAs(email).then(() => location.assign("/")).catch((e) => setError(e instanceof Error ? e.message : "Could not view as"));
  };
  const viewAsButton = (a: { email: string; role: string; disabled: boolean }) =>
    !a.disabled && a.email !== me.email && (me.role === "admin" || a.role !== "admin") &&
    <button className="btn fleet-view-as" onClick={(e) => { e.stopPropagation(); viewAs(a.email); }}
      onKeyDown={(e) => e.stopPropagation()} title={`Use the app as ${a.email}, until Stop`}>View as</button>;

  for (const group of groups) {
    const key = accountKey(group.email);
    const owned = resources.filter((r) => r.owner === group.email);
    const shared = resources.filter((r) => r.owner !== group.email && r.shares.some((s) => s.email === group.email));
    const matchesGroup = !q || `${group.email} ${group.account?.role ?? "missing owner"}`.toLowerCase().includes(q);
    const visibleOwned = owned.filter((r) => matchesGroup || resourceText(r).includes(q));
    const visibleShared = shared.filter((r) => matchesGroup || resourceText(r).includes(q));
    if (!matchesGroup && visibleOwned.length === 0 && visibleShared.length === 0) continue;
    const a = group.account;
    rows.push({ key, level: 1, title: group.email || "Unassigned resources", kind: a ? "Account" : "Missing owner",
      branch: visibleOwned.length + visibleShared.length > 0,
      relation: <>{owned.filter((r) => r.kind === "computer").length} computers · {owned.filter((r) => r.kind === "kvm").length} KVMs owned
        <small>{shared.length} received share references</small></>,
      state: a ? <>{a.disabled ? "Disabled" : "Enabled"} · {a.role}{a.superadmin ? " · superadmin" : ""}
        <small>{a.role === "admin" ? "Admin access to all resources" : "Ownership and explicit shares"}</small>
        {viewAsButton(a)}</> : "Account no longer present",
      last: a?.last_access ?? null });
    if (!open(key)) continue;
    for (const r of visibleOwned) {
      const rk = resourceKey(r.kind, r.id);
      const cable = "on_cable" in r ? r.on_cable : null;
      rows.push({ key: rk, parent: key, level: 2, title: r.name, kind: r.kind === "kvm" ? "KVM" : `Computer · ${"os" in r ? r.os : ""}`,
        branch: r.path_ids.length > 0, relation: <>Owner: {r.owner || "unassigned"}{shares(r)}
          {cable !== null && <small>On cable: {cable ? computerNames.get(cable) ?? `Missing computer (${cable})` : "Not assigned"}</small>}</>,
        state: state(r), last: r.last_access });
      if (!open(rk)) continue;
      for (const id of r.path_ids) {
        const p = paths.get(id);
        if (!p) continue;
        rows.push({ key: `${rk}/path:${id}`, parent: rk, level: 3,
          title: r.kind === "kvm" ? computerNames.get(p.computer_id) ?? p.computer_id : p.kind === "kvm" ? kvmNames.get(p.kvm_id) ?? "Missing KVM" : p.kind.toUpperCase(),
          kind: `${p.kind.toUpperCase()} ${r.kind === "kvm" ? "mapping reference" : "connection"} · ${p.id}`,
          relation: <>{p.kind === "kvm" ? <>Cable: {p.cable === "current" ? "this computer" : p.cable === "other" ? "another computer" : "unknown"}</> : "Direct connection"}
            <small>{r.kind === "kvm" ? "Same connection as under its computer; not counted again" : `Computer: ${r.name}`}</small></>,
          state: <>{stale ? "Snapshot stale" : sessionLabel[p.session]}{!stale && p.activity && <small>{p.activity} · power unknown</small>}
            {!stale && p.hold && p.hold.expires_at > now && <small>Held by {p.hold.holder} · {p.hold.via}<br />Heartbeat {stamp(p.hold.heartbeat)}</small>}
            {!stale && (!p.hold || p.hold.expires_at <= now) && p.last_heartbeat !== null && <small>Expired heartbeat: {stamp(p.last_heartbeat)}</small>}</>,
          last: p.last_access });
      }
    }
    for (const r of visibleShared) {
      const share = r.shares.find((s) => s.email === group.email)!;
      rows.push({ key: `${key}/share:${r.kind}:${r.id}`, parent: key, level: 2, title: r.name,
        kind: `Shared ${r.kind === "kvm" ? "KVM" : "computer"} reference`,
        relation: <>From {r.owner || "unassigned"} · {share.level}<small>Granted by {share.by} · {stamp(share.at)}</small></>,
        state: state(r), last: r.last_access, jump: resourceKey(r.kind, r.id), jumpOwner: accountKey(r.owner) });
    }
  }

  const focus = (key: string) => { setFocused(key); rowRefs.current.get(key)?.focus(); };
  const activate = (row: Row) => {
    if (row.jump) {
      setQuery(""); setExpanded((old) => new Set([...old, row.jumpOwner!, row.jump!]));
      setFocused(row.jump); jumpPending.current = row.jump;
    } else if (row.branch && !q) toggle(row.key);
  };
  const onKey = (e: KeyboardEvent, row: Row, index: number) => {
    let next: string | undefined;
    if (e.key === "ArrowDown") next = rows[index + 1]?.key;
    else if (e.key === "ArrowUp") next = rows[index - 1]?.key;
    else if (e.key === "Home") next = rows[0]?.key;
    else if (e.key === "End") next = rows.at(-1)?.key;
    else if (e.key === "ArrowRight") {
      if (row.branch && !open(row.key)) toggle(row.key); else if (row.branch) next = rows[index + 1]?.key;
    } else if (e.key === "ArrowLeft") {
      if (row.branch && open(row.key) && !q) toggle(row.key); else next = row.parent;
    } else if (e.key === "Enter" || e.key === " ") activate(row);
    else return;
    e.preventDefault(); if (next) focus(next);
  };
  const active = rows.some((r) => r.key === focused) ? focused : rows[0]?.key;

  return <main className="fleet-page">
    <div className="fleet-heading"><div><h1>Account overview</h1><p className="muted">Superadmin · read-only inventory across every account</p></div>
      <button className="btn" onClick={() => setRefresh((n) => n + 1)} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button></div>
    {error && <p role="alert" className="error">{denied ? "Access to the overview is no longer available." : `${error}. Current state unavailable; showing the last snapshot.`}</p>}
    {!data && !error && <p role="status">Loading accounts and resources…</p>}
    {data && <>
      <p className="fleet-counts">{data.counts.accounts} accounts · {data.counts.computers} unique computers · {data.counts.kvms} unique KVMs · {data.counts.paths} connections
        <span className="muted"> · Snapshot {stamp(data.generated_at)}{stale ? " (stale)" : " · refreshes every 15 seconds"}</span></p>
      <p className="fleet-explanation">Resources appear under their owner. Shares and KVM mappings are references, not extra resources.
        An open session is a server connection, not a power or reachability check. Disconnected does not mean offline.
        Last access is the latest recorded admission, run start or retained heartbeat; accounts also include sign-in and API-key use. No record does not mean never used.</p>
      <div className="fleet-tools"><label>Search <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Account, computer, KVM or connection" /></label>
        <button className="btn" disabled={!!q} onClick={() => setExpanded(new Set(allBranches))}>Expand all</button>
        <button className="btn" disabled={!!q} onClick={() => setExpanded(new Set())}>Collapse all</button></div>
      <p id="fleet-help" className="muted fleet-help">Arrow keys navigate; Right/Left expand/collapse; Enter opens a shared resource’s owner row. Search expands matching groups. Scroll the table sideways on small screens.</p>
      <div className="fleet-scroll"><table className="fleet-grid" role="treegrid" aria-label="Accounts, computers and KVMs" aria-describedby="fleet-help" aria-colcount={4} aria-rowcount={rows.length + 1}>
        <thead><tr role="row" aria-rowindex={1}>{["Account / resource", "Ownership, sharing & mapping", "Current state / hold", "Last recorded access"].map((h) => <th role="columnheader" key={h}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={row.key} role="row" aria-rowindex={index + 2} aria-level={row.level}
          aria-expanded={row.branch ? open(row.key) : undefined} tabIndex={active === row.key ? 0 : -1}
          ref={(el) => { if (el) rowRefs.current.set(row.key, el); else rowRefs.current.delete(row.key); }}
          onFocus={() => setFocused(row.key)} onKeyDown={(e) => onKey(e, row, index)} className={row.level === 1 ? "fleet-account" : ""}>
          <td role="gridcell" style={{ paddingInlineStart: 12 + (row.level - 1) * 18 }}>
            {row.branch || row.jump ? <button className="fleet-name" tabIndex={-1} onClick={() => { focus(row.key); activate(row); }}
              aria-label={row.jump ? `Show owner of ${row.title}` : `${open(row.key) ? "Collapse" : "Expand"} ${row.title}`}>
              <span aria-hidden="true">{row.jump ? "↗" : open(row.key) ? "▾" : "▸"}</span> {row.title}</button> : <strong>{row.title}</strong>}
            <small>{row.kind}</small></td>
          <td role="gridcell">{row.relation}</td><td role="gridcell">{row.state}</td><td role="gridcell"><AccessTime value={row.last} /></td>
        </tr>)}</tbody>
      </table></div>
      {rows.length === 0 && <p role="status">{q ? "No matching accounts or resources." : "No accounts or resources yet."}</p>}
    </>}
  </main>;
}
